// Libs
import React, { useEffect, useState, useRef, Fragment } from "react";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { ListingService } from "shared_components";
import { IntercomAPI } from "react-intercom";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  useQueryParams,
  useQueryParam,
  StringParam,
  ArrayParam
} from "use-query-params";

// Components
import Header from "newDropshipperApp/pages/search/Header";
import Breadcrumb from "newDropshipperApp/pages/search/Breadcrumb";
import ListingCards from "newDropshipperApp/pages/search/ListingCards";
import QuickFilters from "newDropshipperApp/pages/search/QuickFilters";
import Onboarding from "newDropshipperApp/component/Onboarding";
import Subcategories from "newDropshipperApp/pages/search/Subcategories";
import ActiveFilters from "newDropshipperApp/pages/search/ActiveFilters";
import CategoriesCards from "newDropshipperApp/pages/search/CategoriesCards";
// import TrendingCollections from "newDropshipperApp/component/TrendingCollections";
import { default as Loadable } from "components/_Shared/commonLoadable";
import ReactivationBanner from "newDropshipperApp/component/bannerConductor/ReactivationBanner";
import CardDeclineError from "components/CardDeclineError/CardDeclineError";

// Actions
import { getListings } from "newDropshipperApp/module/listings";
import { getSuppliers } from "newDropshipperApp/module/suppliers/aliases";
import { getCollections } from "newDropshipperApp/module/suppliers/collections";
import { getPushedListingCustomizationsIds } from "newDropshipperApp/module/pushedListingCustomizations/ids";

// Images
import NoProducts from "assets/no-search.svg";

// Style
import {
  MessageStyle,
  SearchContainer,
  CollectionBanner,
  CollectionContainer,
  NoProductsFoundImage,
  NoProductsFoundContainer
} from "./SearchStyle";
import { gaEvent } from "../utils/trackEvents";

function Search({
  listings,
  collections,
  getListings,
  getSuppliers,
  showPromotion,
  getCollections,
  confirmationUrl,
  suppliersAliases,
  isListingModalOpen,
  currentPromotionType,
  dropshipperDataWasFetched,
  getPushedListingCustomizationsIds,
  goalsNumberOfProduct,
  abTests
}) {
  // Managing state in URL query parameters with serialization
  const queryParams = useQueryParams({
    sort_by: StringParam,
    premium: StringParam,
    item_cost: ArrayParam,
    category: StringParam,
    keywords: StringParam,
    ships_to: StringParam,
    ships_from: StringParam,
    subcategory: StringParam,
    best_selling: StringParam,
    supplier_name: StringParam,
    shipping_time: StringParam,
    shipping_under: StringParam,
    branded_invoicing: StringParam,
    supplier_verified: StringParam
  });
  const [query, setQuery] = queryParams;

  // Use translation hook
  const { t } = useTranslation();

  // We are using the listing_id query string to open the product details modal
  const [listingId, setListingId] = useQueryParam("listing_id", StringParam);

  // If the collection_id is present on the URL, it will trigger the collection view
  const collection_id = useQueryParam("collection_id", StringParam)[0];

  // Getting categories card DOM nodes.
  const categoriesCardsRef = useRef();

  // It resets the InfiniteScroll page when a new search is triggered
  const [resetPageKey, setResetPageKey] = useState(null);

  // Getting categories cards scroll position
  const [categoriesCardsPosY, setCategoriesCardsPosY] = useState(null);

  // Getting categories cards height size
  const [
    categoriesCardsOffsetHeight,
    setCategoriesCardsOffsetHeight
  ] = useState();

  // CategoriesCards scroll position
  useScrollPosition(
    ({ currPos }) => {
      setCategoriesCardsPosY(currPos.y);
      setCategoriesCardsOffsetHeight(categoriesCardsRef.current.offsetHeight);
    },

    [],
    categoriesCardsRef,
    false
  );

  // Triggering the fetchData function every time the query parameters changes
  useEffect(
    () => {
      const fetchData = async () => {
        setResetPageKey(Math.random());
        await getListings({ ...query, page: 1, collection_id });

        collection_id && setQuery({ category: null }, "pushIn");
        query.keywords &&
          gaEvent({
            category: "Search Page",
            action: "Search",
            option: { query: query.keywords }
          });

        /**
         * RED FLAG - We need to call these two endpoints to know what product is imported or pushed
         */
        await ListingService.setImportedListings();
        await getPushedListingCustomizationsIds();
      };
      fetchData();
    },
    [
      query,
      collection_id,
      getListings,
      ListingService,
      getPushedListingCustomizationsIds,
      JSON.stringify(abTests)
    ]
  );

  // This is the initail load - changes title, get suppliers 
  // ? Why do we set to rerun this when suppliersAliases, getSuppliers, collections, getCollections changes?
  useEffect(
    () => {
      document.title = "Search - Spocket";
      IntercomAPI("trackEvent", "Visited: New Search");

      !suppliersAliases.length && getSuppliers();
      !collections.length && getCollections();
    },
    [suppliersAliases, getSuppliers, collections, getCollections]
  );

  // If a listingId is selected, change the listing modal status
  useEffect(
    () => {
      ListingService.openModal(listingId);
    },
    [listingId, ListingService]
  );

  /**
   *
   * When the listing_id is updated, the window.history object is updated, and all the query string will show up again.
   *
   */
  useEffect(
    () => {
      isListingModalOpen === false && setListingId(null);
    },
    [isListingModalOpen, setListingId]
  );

  // Method called by the ListingCards child when a new page of listings should be loaded
  const appendNextPage = nextPage => {
    getListings({ ...query, page: nextPage, collection_id });
  };

  // Clears all active queries and sets query object to original/empty state
  const clearAll = () => {
    const queryAllNull = query;
    Object.keys(query).forEach(key => {
      queryAllNull[key] = null;
    });

    setQuery(queryAllNull, "pushIn");
  };

  // Renders onboarding component
  const renderOnboarding = () => {
    if (abTests.length > 0 && !currentPromotionType) {
      return <Onboarding />;
    }
  };

  // Use collection_id to return Collection banner image 
  const getCollectionImageById = () => {
    if (collections.length > 0) {
      const selectedCollection = collections.find(
        collection => collection.id === collection_id
      );

      return selectedCollection ? selectedCollection.banner_image : "";
    }
  };

  // While listings are being fetched, show message
  // If no listings were found, return message
  const renderProductMessage = () => {
    if (listings.isFetching) {
      return renderMessage();
    } else {
      return renderNoProductsFound();
    }
  };

  const renderMessage = () => {
    if (listings.hasMore) { 
      return (
        <MessageStyle>
          <div>{t("Search.Searching.Title")}</div>
          <div>{t("Search.Searching.Description")}</div>
        </MessageStyle>
      );
    }
  };

  const renderNoProductsFound = () => {
    return (
      <NoProductsFoundContainer>
        <MessageStyle>
          <div>
            {t("Search.NoProductsFound.Title")}
            {query.keywords ? ` ${t("Search.NoProductsFound.For")} '${query.keywords}.'` : "."}
          </div>
          <div>
            {t("Search.NoProductsFound.Description")}
          </div>
        </MessageStyle>
        <NoProductsFoundImage>
          <img src={NoProducts} alt="Man looking at a magnifying glass" />
        </NoProductsFoundImage>
      </NoProductsFoundContainer>
    );
  };

  const hideCategoriesCards = () => {
    if (!(query.category || query.keywords || collection_id)) {
      return {
        height: "auto",
        opacity: 1,
        visibility: "visible"
      };
    } else {
      return {
        height: "0px",
        opacity: 0,
        visibility: "hidden"
      };
    }
  };

  return (
    <SearchContainer>
      <Fragment>
        <CardDeclineError />
        <ReactivationBanner />
        {renderOnboarding()}
      </Fragment>

      <Header
        categoriesCardsPosY={categoriesCardsPosY}
        categoriesCardsOffsetHeight={categoriesCardsOffsetHeight}
      />

      <div ref={categoriesCardsRef}>
        <CategoriesCards style={hideCategoriesCards()} />
      </div>

      <Breadcrumb collections={collections} />
      {query.category && <Subcategories />}
      {!collection_id && <QuickFilters />}

      {/* {!collection_id && (
        <TrendingCollectionsStyle>
          <TrendingCollections
            searchCollection={data =>
              setCollection_id(data.collection.id, "pushIn")
            }
          />
        </TrendingCollectionsStyle>
      )} */}

      <ActiveFilters
        query={query}
        setQuery={setQuery}
        clearAll={clearAll}
        style={{ marginBottom: "32px" }}
      />

      {collection_id &&
        getCollectionImageById() && (
          <CollectionContainer>
            <CollectionBanner
              src={getCollectionImageById()}
              alt="Collection Banner"
            />
          </CollectionContainer>
        )}
        
      <Loadable active={listings.isFetching}>
        {listings.products.length > 0 ? (
          <ListingCards
            listings={listings}
            resetPageKey={resetPageKey}
            appendNextPage={appendNextPage}
          />
        ) : (
          renderProductMessage()
        )}
      </Loadable>
    </SearchContainer>
  );
}

Search.propTypes = {
  listings: PropTypes.object.isRequired,
  getListings: PropTypes.func.isRequired,
  getPushedListingCustomizationsIds: PropTypes.func.isRequired
};

function mapStateToProps(state) {
  const { aliases, collections } = state.supplier;
  const { dropshipperData, currentSubscription } = state.settings;

  const getGoalsNumberOfProduct = () =>
    ((dropshipperData || 0).goals || 0).number_of_products || 0;

  return {
    listings: state.listings,
    collections: collections.data,
    suppliersAliases: aliases.data,
    showPromotion: dropshipperData.show_promotion,
    goalsNumberOfProduct: getGoalsNumberOfProduct(),
    abTests: state.settings.dropshipperData.ab_tests,
    confirmationUrl: currentSubscription.confirmation_url,
    currentPromotionType: dropshipperData.current_promotion_type || "",
    isListingModalOpen:
      state.sharedComponents.listings.listingForModal === null ? false : true,
    dropshipperDataWasFetched:
      Object.keys(dropshipperData).length > 1 ? true : false
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      getListings,
      getSuppliers,
      getCollections,
      getPushedListingCustomizationsIds
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Search);



// WEBPACK FOOTER //
// ./src/newDropshipperApp/pages/Search.js